import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal, Select, Input, Button, List, Tag, Empty, Spin, Alert, Popconfirm, message,
  Upload, Image, Space,
} from 'antd';
import { DeleteOutlined, PlusOutlined, PaperClipOutlined, FileOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/config';
import { authHeaders } from '../../utils/auth';

const { TextArea } = Input;

const tarihMetni = (deger) => {
  if (!deger) return '';
  const d = new Date(deger);
  if (isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', {
    hour: '2-digit', minute: '2-digit',
  })}`;
};

const boyutMetni = (b) => {
  if (!b) return '';
  if (b > 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(b / 1024)} KB`;
};

const EklerGorunumu = ({ n, ekSil, boyutMetni }) => {
  const ekler = n.ekler || [];
  if (!ekler.length) return null;
  const resimler = ekler.filter((e) => e.resim && e.url);
  const digerleri = ekler.filter((e) => !(e.resim && e.url));
  return (
    <div style={{ marginTop: 6 }}>
      {resimler.length > 0 && (
        <Image.PreviewGroup>
          <Space wrap size={6}>
            {resimler.map((e) => (
              <div key={e.id} style={{ position: 'relative' }}>
                <Image
                  src={e.url}
                  alt={e.dosya_adi || ''}
                  width={72}
                  height={72}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
                <Popconfirm title="Ek silinsin mi?" okText="Sil" cancelText="Vazgeç" onConfirm={() => ekSil(n.id, e.id)}>
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(255,255,255,0.8)' }}
                  />
                </Popconfirm>
              </div>
            ))}
          </Space>
        </Image.PreviewGroup>
      )}
      {digerleri.map((e) => (
        <div key={e.id} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileOutlined />
          {e.url
            ? <a href={e.url} target="_blank" rel="noopener noreferrer">{e.dosya_adi || 'dosya'}</a>
            : <span>{e.dosya_adi || 'dosya'}</span>}
          <span style={{ color: '#8c8c8c' }}>{boyutMetni(e.boyut)}</span>
          <Popconfirm title="Ek silinsin mi?" okText="Sil" cancelText="Vazgeç" onConfirm={() => ekSil(n.id, e.id)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ))}
    </div>
  );
};

/**
 * Enerji süreci notları — adım bazlı.
 *
 * Bilinçli olarak sade: hangi adım için (dropdown) + not metni (textarea) +
 * altında notların listesi. Adım işleme, red ve onay akışları BURADA DEĞİL;
 * onlar kendi ekranlarında. Önceki tek-alanlı yapıda her kayıt bir öncekini
 * eziyordu ve notun hangi adıma ait olduğu kaybediliyordu.
 *
 * @param {object} record - Enerji Süreçleri satırı ({ lokasyon, lokasyon_adi })
 * @param {Array}  adimlar - GET /enerji/tanim'dan gelen adım listesi
 * @param {func}   onChanged - not eklenince/silinince çağrılır (rozet tazelensin)
 */
const EnerjiNotlarModal = ({ open, record, adimlar = [], onClose, onChanged }) => {
  const lokasyon = record?.lokasyon ?? record?.maptriksID;

  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [notlar, setNotlar] = useState([]);
  const [uyari, setUyari] = useState('');
  const [filtreAdim, setFiltreAdim] = useState(null);

  const [yeniAdim, setYeniAdim] = useState(null);
  const [yeniMetin, setYeniMetin] = useState('');
  // 20.08 madde 12: not ekleri / fotoğraflar (önce not, sonra dosyalar tek tek yüklenir)
  const [dosyalar, setDosyalar] = useState([]);
  const [ekDestegi, setEkDestegi] = useState(true);
  const [ekYukleniyor, setEkYukleniyor] = useState(null); // not id (mevcut nota ek ekleme)

  const ekYukle = async (notId, file) => {
    const fd = new FormData();
    fd.append('dosya', file);
    const cfg = authHeaders();
    await axios.post(`${API_BASE_URL}/enerji/${lokasyon}/notlar/${notId}/ek`, fd, {
      ...cfg, headers: { ...(cfg.headers || {}), 'Content-Type': 'multipart/form-data' },
    });
  };

  // Karar adımı (Enerji Tipi) not hedefi olarak anlamsız; listeden çıkarılır.
  const adimSecenekleri = useMemo(
    () => [
      { value: '', label: 'Genel (adım bağımsız)' },
      ...(adimlar || [])
        .filter((a) => !a.karar_adimi)
        .map((a) => ({ value: a.anahtar, label: a.ad })),
    ],
    [adimlar]
  );

  const adimAdi = useCallback(
    (anahtar) => (adimlar || []).find((a) => a.anahtar === anahtar)?.ad || null,
    [adimlar]
  );

  const getir = useCallback(async () => {
    if (!lokasyon) return;
    setYukleniyor(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/enerji/${lokasyon}/notlar`, authHeaders());
      setNotlar(res.data?.notlar || []);
      setUyari(res.data?.uyari || '');
      if (typeof res.data?.ek_destegi === 'boolean') setEkDestegi(res.data.ek_destegi);
    } catch (e) {
      setUyari(e?.response?.data?.detail || 'Notlar alınamadı');
      setNotlar([]);
    } finally {
      setYukleniyor(false);
    }
  }, [lokasyon]);

  useEffect(() => {
    if (open) {
      setYeniMetin('');
      setYeniAdim(null);
      setFiltreAdim(null);
      setDosyalar([]);
      getir();
    }
  }, [open, getir]);

  const ekle = async () => {
    const metin = yeniMetin.trim();
    if (!metin) {
      message.warning('Not metni boş olamaz');
      return;
    }
    setKaydediyor(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/enerji/${lokasyon}/notlar`,
        { adim: yeniAdim || null, not_metni: metin },
        authHeaders()
      );
      const notId = res.data?.id;
      let hatali = 0;
      if (notId && dosyalar.length) {
        for (const f of dosyalar) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await ekYukle(notId, f);
          } catch (e) {
            hatali += 1;
            message.error(`${f.name}: ${e?.response?.data?.detail || 'yüklenemedi'}`);
          }
        }
      }
      setYeniMetin('');
      setDosyalar([]);
      await getir();
      onChanged?.();
      message.success(hatali ? `Not eklendi (${hatali} ek yüklenemedi)` : 'Not eklendi');
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Not eklenemedi');
    } finally {
      setKaydediyor(false);
    }
  };

  const mevcutNotaEkle = async (notId, file) => {
    setEkYukleniyor(notId);
    try {
      await ekYukle(notId, file);
      await getir();
      onChanged?.();
      message.success('Ek yüklendi');
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Ek yüklenemedi');
    } finally {
      setEkYukleniyor(null);
    }
    return false;
  };

  const ekSil = async (notId, ekId) => {
    try {
      await axios.delete(`${API_BASE_URL}/enerji/${lokasyon}/notlar/${notId}/ek/${ekId}`, authHeaders());
      await getir();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Ek silinemedi');
    }
  };



  const sil = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/enerji/${lokasyon}/notlar/${id}`, authHeaders());
      await getir();
      onChanged?.();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Not silinemedi');
    }
  };

  const gosterilen = filtreAdim == null
    ? notlar
    : notlar.filter((n) => (n.adim || '') === filtreAdim);

  return (
    <Modal
      title={`Notlar${record?.lokasyon_adi ? ` — ${record.lokasyon_adi}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnHidden
    >
      {uyari && <Alert type="warning" showIcon message={uyari} style={{ marginBottom: 12 }} />}

      {/* Not ekleme: adım seçimi + metin */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        <Select
          placeholder="Hangi adım için?"
          options={adimSecenekleri}
          value={yeniAdim ?? ''}
          onChange={(v) => setYeniAdim(v || null)}
          style={{ width: '100%' }}
        />
        <TextArea
          rows={3}
          placeholder="Notunuz..."
          value={yeniMetin}
          onChange={(e) => setYeniMetin(e.target.value)}
          maxLength={2000}
          showCount
        />
        {ekDestegi && (
          <Upload
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            fileList={dosyalar.map((f, i) => ({ uid: f.uid || String(i), name: f.name, status: 'done' }))}
            beforeUpload={(file) => { setDosyalar((d) => [...d, file]); return false; }}
            onRemove={(f) => setDosyalar((d) => d.filter((x, i) => (x.uid || String(i)) !== f.uid))}
          >
            <Button icon={<PaperClipOutlined />} size="small">Ek / Fotoğraf ekle</Button>
            <span style={{ marginLeft: 8, fontSize: 12, color: '#8c8c8c' }}>
              Resim, PDF, Word, Excel — en fazla 15 MB
            </span>
          </Upload>
        )}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={kaydediyor}
          onClick={ekle}
          style={{ alignSelf: 'flex-end' }}
        >
          Not Ekle{dosyalar.length ? ` (+${dosyalar.length} ek)` : ''}
        </Button>
      </div>

      {/* Notlar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>Notlar ({notlar.length})</strong>
        <Select
          size="small"
          allowClear
          placeholder="Adıma göre filtrele"
          style={{ width: 200 }}
          value={filtreAdim}
          onChange={(v) => setFiltreAdim(v ?? null)}
          options={adimSecenekleri}
        />
      </div>

      {yukleniyor ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : gosterilen.length === 0 ? (
        <Empty description="Not yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          size="small"
          dataSource={gosterilen}
          style={{ maxHeight: 320, overflowY: 'auto' }}
          renderItem={(n) => (
            <List.Item
              actions={[
                ekDestegi && (
                  <Upload
                    key="ek"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    showUploadList={false}
                    beforeUpload={(file) => mevcutNotaEkle(n.id, file)}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<PaperClipOutlined />}
                      loading={ekYukleniyor === n.id}
                      title="Bu nota ek/fotoğraf ekle"
                    />
                  </Upload>
                ),
                <Popconfirm
                  key="sil"
                  title="Not silinsin mi?"
                  okText="Sil"
                  cancelText="Vazgeç"
                  onConfirm={() => sil(n.id)}
                >
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>,
              ].filter(Boolean)}
            >
              <List.Item.Meta
                title={
                  <span style={{ fontSize: 12 }}>
                    {n.adim ? <Tag color="blue">{adimAdi(n.adim) || n.adim}</Tag> : <Tag>Genel</Tag>}
                    <span style={{ color: '#8c8c8c' }}>
                      {n.ekleyen_adi || 'Bilinmiyor'} · {tarihMetni(n.eklenme_tarihi)}
                    </span>
                  </span>
                }
                description={
                  <div>
                    <span style={{ whiteSpace: 'pre-wrap', color: 'inherit' }}>{n.not_metni}</span>
                    <EklerGorunumu n={n} ekSil={ekSil} boyutMetni={boyutMetni} />
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};

export default EnerjiNotlarModal;
